import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PhoneOutgoing, Send, Headphones } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ts, P } from '../api';
import { Page, Section, Field, Select, Table, Td, Pill, Empty, Loading, DISPOSITIONS, useLoad, useTS, when } from '../ui';
import { useAppCalls } from '../AppCalls';

export default function TSPhone() {
  const session = useTS();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || 'calls');
  return (
    <Page title="Phone & SMS" subtitle="Calls ring your own phone first, then connect the customer from your company number.">
      <div className="flex gap-1 border-b border-border">
        {[['calls', 'Calls'], ['team', 'Team calls (free)'], ['sms', 'Text messages']].map(([k, l]) => (
          <button key={k} data-testid={`ts-phone-tab-${k}`} onClick={() => setTab(k)} className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === k ? 'border-violet-500 text-foreground font-medium' : 'border-transparent text-muted-foreground'}`}>{l}</button>
        ))}
      </div>
      {tab === 'calls' && <Calls session={session} initialTo={params.get('to') || ''} leadId={params.get('lead') || ''} />}
      {tab === 'team' && <TeamCalls session={session} />}
      {tab === 'sms' && <Messages session={session} initialTo={params.get('to') || ''} />}
    </Page>
  );
}

const APP_CALL_LABELS = {
  ringing: 'Ringing', answered: 'In progress', completed: 'Completed', declined: 'Declined', canceled: 'Canceled',
  no_answer: 'No answer', offline: 'Offline', busy: 'Busy', disconnected: 'Dropped',
};

function TeamCalls({ session }) {
  const app = useAppCalls();
  const staff = useLoad(() => ts.staff());
  const history = useLoad(() => ts.appCalls(), [app.call?.id, app.call?.status]);
  const names = Object.fromEntries((staff.data || []).map(m => [m.id, m.full_name]));
  const colleagues = (staff.data || []).filter(m => m.id !== session.member?.id && (!m.status || m.status === 'active'));
  return (
    <div className="space-y-4">
      <Section title="Call a colleague">
        <p className="text-xs text-muted-foreground">Calls between team members go app to app over the internet. They're free and never use your plan's minutes.</p>
        {!app.ready && <p className="text-xs text-amber-700 dark:text-amber-400">Connecting to team calling…</p>}
        {staff.loading ? <Loading /> : colleagues.length === 0 ? <Empty>No colleagues yet.</Empty> : (
          <ul className="divide-y divide-border" data-testid="ts-team-list">
            {colleagues.map(m => {
              const on = app.online.has(m.id);
              return (
                <li key={m.id} className="py-2 flex items-center gap-3 text-sm">
                  <span className={`w-2 h-2 rounded-full ${on ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
                  <span className="flex-1 text-foreground">{m.full_name}</span>
                  <span className="text-xs text-muted-foreground">{on ? 'Online' : 'Offline'}</span>
                  <Button size="sm" variant="outline" disabled={!on || !app.ready || !!app.call} onClick={() => app.start(m)} data-testid={`ts-team-call-${m.id}`}>
                    <Headphones className="w-4 h-4 mr-1" /> Call free
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
      <Section title="Recent team calls">
        {(history.data || []).length === 0 ? <Empty>No team calls yet.</Empty> : (
          <Table head={['When', 'With', 'Result', 'Length']}>
            {history.data.map(c => {
              const other = c.caller_member_id === session.member?.id ? c.callee_member_id : c.caller_member_id;
              return <tr key={c.id}><Td>{when(c.created_at)}</Td><Td>{names[other] || 'Colleague'}</Td><Td>{APP_CALL_LABELS[c.status] || c.status}</Td><Td>{c.duration_seconds ? `${Math.ceil(c.duration_seconds / 60)} min` : '—'}</Td></tr>;
            })}
          </Table>
        )}
      </Section>
    </div>
  );
}

function Calls({ session, initialTo, leadId }) {
  const [to, setTo] = useState(initialTo);
  const [scope, setScope] = useState('mine');
  const [placing, setPlacing] = useState(null);
  const history = useLoad(() => ts.calls(scope), [scope]);
  const supervisor = session.can(P.SUPERVISE);

  const call = async (e) => {
    e.preventDefault();
    try {
      const r = await ts.placeCall({ to, lead_id: leadId || undefined });
      setPlacing(r);
      toast.success('Your phone is ringing. Answer it to connect the customer.');
      setTimeout(history.reload, 1500);
    } catch (err) {
      toast.error(err.message);
    }
  };
  const dispose = async (callId, d) => {
    try { await ts.disposition(callId, d); history.reload(); } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-4">
      {session.can(P.CALL) && (
        <Section title="Make a call">
          <form onSubmit={call} className="flex flex-wrap gap-2 items-end">
            <Field label="Customer number"><Input value={to} onChange={e => setTo(e.target.value)} placeholder="+1 555 123 4567" className="w-56" data-testid="ts-dial-to" /></Field>
            <Button type="submit" disabled={!to} data-testid="ts-dial"><PhoneOutgoing className="w-4 h-4 mr-1" /> Call</Button>
          </form>
          <p className="text-xs text-muted-foreground">
            We ring {session.member.forward_phone || 'the phone in your settings'} first. The customer sees your company number. Calls follow your company's calling hours and do-not-call list.
          </p>
          {placing && <p className="text-sm" data-testid="ts-call-status">Status: <Pill tone="blue">{placing.status.replace(/_/g, ' ')}</Pill>{placing.simulated && <Pill tone="amber">Test mode</Pill>}</p>}
        </Section>
      )}
      <Section title="Call history" actions={supervisor && <Select value={scope} onChange={setScope} options={[['mine', 'My calls'], ['all', 'Whole team']]} />}>
        {history.loading ? <Loading /> : (history.data || []).length === 0 ? <Empty>No calls yet.</Empty> : (
          <Table head={['When', 'Direction', 'Customer', 'Employee', 'Status', 'Length', 'Result']}>
            {history.data.map(c => (
              <tr key={c.id}>
                <Td>{when(c.created_at)}</Td>
                <Td>{c.direction}{c.handled_by === 'ai' && <Pill tone="blue">AI</Pill>}</Td>
                <Td>{c.customer_number}</Td>
                <Td>{c.member_name || '—'}</Td>
                <Td>{c.missed ? <Pill tone="red">missed</Pill> : c.status}</Td>
                <Td>{c.duration_seconds || 0}s</Td>
                <Td>
                  {c.member_id === session.member.id || supervisor
                    ? <Select value={c.disposition || ''} onChange={(d) => d && dispose(c.id, d)} options={[['', 'Set result…'], ...DISPOSITIONS]} />
                    : c.disposition || '—'}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </div>
  );
}

function Messages({ session, initialTo }) {
  const convos = useLoad(() => ts.conversations());
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [to, setTo] = useState(initialTo);
  const [body, setBody] = useState('');

  useEffect(() => {
    if (!active) { setThread(null); return; }
    ts.conversation(active).then(setThread).catch(e => toast.error(e.message));
  }, [active]);

  const send = async (e) => {
    e.preventDefault();
    const dest = thread ? thread.conversation.external : to;
    try {
      await ts.sendSms(dest, body);
      setBody('');
      convos.reload();
      if (active) setThread(await ts.conversation(active));
      toast.success('Sent');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Section title="Conversations" className="md:col-span-1">
        <Button size="sm" variant="outline" onClick={() => setActive(null)}>New message</Button>
        {(convos.data || []).length === 0 ? <Empty>No text conversations yet.</Empty> : (
          <ul className="divide-y divide-border">
            {convos.data.map(c => (
              <li key={c.id}>
                <button onClick={() => setActive(c.id)} className={`w-full text-left py-2 px-1 ${active === c.id ? 'bg-muted rounded' : ''}`}>
                  <div className="text-sm font-medium">{c.external}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.last_message}</div>
                  <div className="text-[11px] text-muted-foreground">{c.member_name || 'Unassigned'} · {when(c.last_message_at)}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title={thread ? thread.conversation.external : 'New message'} className="md:col-span-2">
        {thread && (
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="ts-sms-thread">
            {thread.messages.map(m => (
              <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.direction === 'outbound' ? 'ml-auto bg-violet-500/15' : 'bg-muted'}`}>
                <div>{m.body}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{m.direction === 'outbound' ? (m.member_name || 'You') : 'Customer'} · {when(m.created_at)}{m.sent_by_ai ? ' · AI' : ''}</div>
              </div>
            ))}
          </div>
        )}
        {session.can(P.SMS) && (
          <form onSubmit={send} className="space-y-2">
            {!thread && <Input value={to} onChange={e => setTo(e.target.value)} placeholder="Customer number" data-testid="ts-sms-to" />}
            <Textarea value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="Type a message" data-testid="ts-sms-body" />
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Sent from your company number. Texting works only once your number's SMS status is Active.</span>
              <Button type="submit" size="sm" disabled={!body.trim() || (!thread && !to)} data-testid="ts-sms-send"><Send className="w-4 h-4 mr-1" /> Send</Button>
            </div>
          </form>
        )}
      </Section>
    </div>
  );
}
